const API_BASE = process.env.REACT_APP_API_URL || '';

class OrderService {
  async getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async handleResponse(response) {
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}`;
      
      try {
        const errorData = JSON.parse(errorText);
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => err.msg).join(', ');
          }
        }
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }
    return response.json();
  }

  // Get all orders with optional filters
  async getOrders(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.facility_id) {
        queryParams.append('facility_id', filters.facility_id);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.date) {
        queryParams.append('date', filters.date);
      }
      if (filters.customer_id) {
        queryParams.append('customer_id', filters.customer_id);
      }

      const url = `${API_BASE}/api/orders${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  }

  // Get specific order by ID
  async getOrder(orderId) {
    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  }

  // Update order status
  async updateOrderStatus(orderId, status) {
    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ status })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  // Complete order
  async completeOrder(orderId) {
    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/complete`, {
        method: 'POST',
        headers: await this.getAuthHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error completing order:', error);
      throw error;
    }
  }

  // Cancel order
  async cancelOrder(orderId) {
    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: await this.getAuthHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw error;
    }
  }

  // Get payments for order
  async getOrderPayments(orderId) {
    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/payments`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error fetching order payments:', error);
      throw error;
    }
  }

  // Download invoice PDF
  async downloadInvoice(invoiceId) {
    try {
      const response = await fetch(`${API_BASE}/api/invoices/${invoiceId}/pdf`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return response.blob();
    } catch (error) {
      console.error('Error downloading invoice:', error);
      throw error;
    }
  }

  // Resend invoice via email
  async resendInvoiceEmail(invoiceId, email) {
    try {
      const response = await fetch(`${API_BASE}/api/invoices/${invoiceId}/email`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ recipient_email: email })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Error resending invoice email:', error);
      throw error;
    }
  }

  // Format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  // Format date/time
  formatDateTime(dateString) {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Get status label in German
  getStatusLabel(status) {
    const statusMap = {
      'draft': 'Entwurf',
      'in_progress': 'In Bearbeitung',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert'
    };
    return statusMap[status] || status;
  }

  // Get status color
  getStatusColor(status) {
    const colorMap = {
      'draft': '#6c757d',
      'in_progress': '#007bff',
      'completed': '#28a745',
      'cancelled': '#dc3545'
    };
    return colorMap[status] || '#6c757d';
  }
}

export default new OrderService();